import { Button, Divider, Dropdown, Flex, Layout, Menu, Typography } from 'antd'
import { ChevronDown } from 'lucide-react'
import type { PropsWithChildren } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { AvatarWithInitials, BrandWordmark, BRAND_NAME } from 'src/components'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'

import { AppFooter } from '../AppLayoutShell'
import { useUserDropdownItems } from '../AppLayoutShell/hooks'

import { PublicChromeFooter } from './PublicChromeFooter'
import { useStyles } from './styles'

const { Content } = Layout
const { Text } = Typography

type Props = PropsWithChildren<{
  /**
   * Drop the brand row and the footer, leaving only the content slot. Used by
   * the redesigned onboarding, which owns the whole screen and offers no
   * navigation of its own.
   */
  bare?: boolean
}>

/**
 * Minimal authenticated chrome for standalone flows that have no workspace to
 * frame — currently the first-ever workspace creation right after signup.
 * Brand mark + global utilities (theme, language) + user menu, a centered
 * content slot, and the shared footer. No sidebar, no workspace-scoped topbar.
 */
export const FocusedShell = ({ children, bare }: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, removeUserData } = useSessionContext()

  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const u = user?.user
  const fullName = [u?.first_name, u?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  const displayName =
    fullName || u?.email?.split('@')[0] || t('shell.user.fallback')
  const photoSrc = u?.photo?.file ?? undefined

  const userMenuItems = useUserDropdownItems(
    () => navigate(paths.account.profile),
    () => removeUserData()
  )

  // The chrome of the signed-out screens: wordmark above, language and the two
  // legal links below, and nothing in between. The first workspace is created
  // straight after signing up, so the flow should read as a continuation of
  // those screens rather than as the product it has not entered yet — which is
  // also why the product footer and the account menu stay out of it.
  if (bare) {
    return (
      <Layout className={cx(styles.shell, styles.publicShell)}>
        <div className={styles.publicBrandRow}>
          <Link to={paths.home} aria-label={`${BRAND_NAME} home`}>
            <BrandWordmark variant="auth" />
          </Link>
        </div>

        <Content className={styles.bareContent}>{children}</Content>

        <PublicChromeFooter />
      </Layout>
    )
  }

  return (
    <Layout className={styles.shell}>
      <Layout.Header className={styles.topbar}>
        <Flex
          align="center"
          justify="space-between"
          gap="middle"
          style={{ height: '100%' }}
        >
          <span className={styles.brand}>
            <BrandWordmark variant="auth" />
          </span>

          <Flex align="center" gap={8}>
            {/* Language is chosen in profile / workspace settings, not from
                the header (TS-CORE-027). Appearance lives in the account menu. */}
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              open={userMenuOpen}
              onOpenChange={setUserMenuOpen}
              popupRender={() => (
                <div className={styles.userMenu}>
                  <div className={styles.userMenuHeader}>
                    <Flex align="center" gap={12}>
                      <AvatarWithInitials
                        name={fullName || displayName}
                        id={u?.id}
                        size={40}
                        src={photoSrc}
                      />
                      <Flex vertical style={{ minWidth: 0, lineHeight: 1.25 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {displayName}
                        </Text>
                        {u?.email ? (
                          <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                            ellipsis={{ tooltip: u.email }}
                          >
                            {u.email}
                          </Text>
                        ) : null}
                      </Flex>
                    </Flex>
                  </div>
                  <Divider style={{ margin: 0 }} />
                  <Menu
                    items={userMenuItems}
                    selectable={false}
                    onClick={() => setUserMenuOpen(false)}
                    style={{ border: 'none', padding: 4 }}
                  />
                </div>
              )}
            >
              <Button type="text" size="small" className={styles.user}>
                <Flex align="center" gap={8}>
                  <span className={styles.userName} title={displayName}>
                    {displayName}
                  </span>
                  <AvatarWithInitials
                    name={fullName || displayName}
                    id={u?.id}
                    size={24}
                    src={photoSrc}
                  />
                  <ChevronDown
                    size={12}
                    color="var(--ant-color-text-secondary)"
                  />
                </Flex>
              </Button>
            </Dropdown>
          </Flex>
        </Flex>
      </Layout.Header>

      <Content className={styles.content}>{children}</Content>

      <AppFooter />
    </Layout>
  )
}
